import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FIX_MAX_AGE_MS, MAIN_VIEWS, coverPhoto, cycleView, finalizeBatch, freshFix, newBatch, newBatchPhoto, nextView,
  withHandoffRequest,
} from '../../utils/fieldBatches';
import { exifFromFile } from '../../utils/exifGps';
import { grabEncircle, grabFrame, thumbnailOf } from '../../utils/fieldFrame';
import { centreLoop, loopBounds, loopPath, panelSide } from '../../utils/ai/encircle';
import LiveIdentifyPanel from '../ai/LiveIdentifyPanel';
import { FieldTraitGuide, FieldTraitLook } from '../ai/FieldTraitChecks';
import {
  baseMayBeOutOfView, fieldTraitAsks, mayRecordTraits, withTraitAnswer, withTraitAnswers,
} from '../../utils/ai/fieldTraits';
import { acceptBlockedReason } from '../../utils/aiThresholds';
import { srOnly, useHtmlDir, usePrefersReducedMotion } from '../organic/motion';
import { CAMERA_HOLD_MS, EDIT_HOLD_MS } from '../../utils/fieldOutbox';
import { mayRequestAi, warmAi } from '../../utils/aiIdentify';
import { useFieldAutoAi } from '../../utils/fieldAutoAi';
import FieldCaptureSummary, { KEEP } from './FieldCaptureSummary';
import { useActiveMission } from '../../utils/activeMission';
import { clearActiveTarget, useActiveTarget } from '../../utils/activeTarget';
import { distanceLabel, towards } from '../../utils/geo';
import { useCameraStream } from './useCameraStream';
import { useFocusMeter } from '../camera/useFocusMeter';
import { useCameraControls } from '../camera/useCameraControls';
import CameraControls from '../camera/CameraControls';
import DetectorTrial, { DetectorBoxes } from '../camera/DetectorTrial';
import { useDeviceDetector } from '../camera/useDeviceDetector';
import detectorManifest from '../../utils/camera/detect/manifest';
import CaptureModes from '../camera/CaptureModes';
import ShotBadge, { shotLabel } from '../camera/ShotBadge';
import { useCaptureMode } from '../camera/useCaptureMode';
import { evLabel } from '../../utils/camera/capture';
import Dialog from '../ui/Dialog';
import { ZStackReview, ZStackRun } from '../zstack/ZStackPanel';
import { useZStack } from '../zstack/useZStack';
import { dropFromBundles, newBatchBundle } from '../../utils/zstack/bundle';
import { keepAnnotationsFor, mayAnnotate, withAnnotations } from '../../utils/fieldAnnotations';
import FieldAnnotate from './FieldAnnotate';
import useTapFocus from '../camera/useTapFocus';
import FocusRing from '../camera/FocusRing';
import { useFieldGeo } from './FieldGeoProvider';
import FieldMiniMap from '../events/FieldMiniMap';
import { useEventLive } from '../events/EventLiveProvider';
import { FOCUS_CSS, isToday } from './fieldFormat';
import FieldTargetMap from './FieldTargetMap';
import useRoute from '../../utils/routing/useRoute';
import { useTravelMode } from '../../utils/routing/travelMode';
import ReportIssueButton from '../issues/ReportIssueButton';
import { isReadOnlyRefusal } from '../../utils/viewAs';
import { can, isPublicOnly } from '../../utils/permissions';
import { OBSERVATION, useFieldMode } from '../../utils/fieldMode';
import FieldModePill, { FieldModeDialog } from './FieldModePill';
import AiCreditsBadge from '../credits/AiCreditsBadge';
import { useDeviceHeading } from '../camera/useDeviceHeading';
import { headingForPhoto } from '../../utils/camera/heading';
import CameraViewport from '../camera/CameraViewport';
import Sheet from '../ui/Sheet';
import FieldGalleryList from './FieldGalleryList';
import ChatHub from '../chat/ChatHub';
import { ChatIcon, useChatUnread } from '../chat/ChatButton';
import useFieldSheet, { SHEET_AT_EDGE } from './useFieldSheet';
import FieldTasksSheet from './FieldTasksSheet';
import { mayHaveTasks, useFieldTasks } from '../../utils/fieldTasks';

// How long the summary waits for the warm-up's answer before it asks anyway.
const WARM_WAIT_MS = 3000;

/**
 * The camera: Field Mode's first and main screen.
 *
 * Opened, it shows the viewfinder at once. A specimen is recorded without
 * typing a word: Snap files each photo under the view it shows (top, bottom,
 * habitat, then extras) with when and where it was taken, and Save finishes
 * the batch -- the outbox does the rest in the background while the camera
 * keeps running for the next one.
 *
 * Exactly four main buttons: Back, Gallery, Snap, Save (the "new specimen"
 * button: it saves this one and starts the next). The view chip, the tray of
 * this specimen's photos and a Map chip beside the GPS chip are the only
 * other controls, and there is nothing to type here at all.
 *
 * THREE SHEETS come in over the camera from its edges (2026-10-21;
 * docs/FIELD_AI_VISION_SPEC.md Part II): the collection gallery from the
 * right (FieldGalleryList, the gallery page's own list), messages from the
 * left (components/chat/ChatHub, for whoever has chat) and the camera's
 * controls from the bottom. Each has a labelled handle on its edge -- the way
 * in that always works -- and a swipe in from that edge is the fast one
 * (components/camera/CameraViewport.js; Android and iOS keep the very edges
 * for their own gestures, so the handle comes first). The edges are physical:
 * the gallery is on the right in Hebrew too. The open sheet is in the address
 * (?sheet=, useFieldSheet.js), so the phone's Back closes it rather than
 * leaving the camera. The bar's Gallery button opens the gallery sheet; a
 * card in it opens the batch editor, whose Back returns to the camera with
 * the sheet still open. The camera keeps running under a sheet.
 *
 * A FOURTH, the Tasks sheet (FieldTasksSheet.js; plan phase 6), has no edge:
 * the Tasks (n) chip in the first row and the gallery sheet's Tasks button
 * open it, and the mission chip opens it on that mission's card. Missions
 * assigned to the person, their own open cards and their live events;
 * ?sheet=tasks&mission=<id> (the assignment email's link) opens it on a card.
 *
 * The "Focus & light" chip opens the controls sheet
 * (components/camera/CameraControls.js): the focus meter, and what this
 * phone's camera lets a page set -- exposure, focus (automatic or a
 * distance), its light -- else a digital brightness applied to the preview
 * and to every photo snapped while it is set. It leaves the picture undimmed
 * above it. A tap on the picture focuses there where the camera can. A dot on
 * the chip says something is changed from automatic, sheet open or not. Not a
 * fifth button. The sheet is the Pro drawer too (2026-10-22;
 * docs/FIELD_AI_VISION_SPEC.md section 10): the focus and exposure locks,
 * shutter, ISO, white balance and zoom where the camera offers them (two
 * fingers pinched on the picture zoom as well), and the capture mode -- a
 * burst keeps its sharpest frame; a bracket files its three photos, the
 * one as set under the view and the darker and brighter as extras. At its
 * top, two quick actions (2026-10-26; docs/UI_HUBS_AND_GESTURES_PLAN.md
 * section 5, D17): Identify (below) and Annotate, which opens this
 * specimen's photos on the annotation workbench's canvas (FieldAnnotate.js)
 * and keeps the marks with the batch, for the outbox's Annotations step.
 * The thermal file is no longer here (D23): the summary and the batch
 * editor keep their Thermal step. And a Z-stack (phase A5; components/zstack/): the
 * shutter starts it -- the sheet steps aside, a bar above the shutter says
 * how far it is -- and its review opens as a dialog; kept, the sharpest
 * layer is filed under the view and the others as extras saying which layer
 * they are, and the batch remembers the stack for the outbox's Bundles step.
 * The Map chip
 * leaves for /map; the unfinished specimen stays, and Back returns here. A
 * finished specimen waits a minute before it is sent (CAMERA_HOLD_MS), so a
 * label or "only a sighting" can still be given from the gallery before a
 * code is minted.
 *
 * A photo that could not be kept on the device is said so where it stays on
 * screen, aloud, and with a buzz unlike the shutter's: the flash and the buzz
 * of the shutter have already said it was taken.
 *
 * The unfinished batch lives in the device store from its first photo, so
 * Back, a closed tab or a flat battery lose nothing: it is here again the
 * next time Field Mode opens.
 *
 * Walking to a target set on the national map (utils/activeTarget.js), a 🎯
 * chip names it with the distance left, and a small map floats over the
 * viewfinder (FieldTargetMap: the position, the target, the line, the
 * distance and bearing), fed by this screen's one GPS watch. The chip shows or
 * hides the map; its × forgets the target. Every batch begun while a target is
 * set is stamped with its record's id, so the collection says which
 * observation it documents. Neither is a fifth button on the bar.
 *
 * With no camera to show (permission refused, plain http, none fitted), Snap
 * opens the phone's own camera through a file input instead. A photo from it
 * is dated and placed by its own EXIF where that says (utils/exifGps.js),
 * else by the moment it came back and the phone's fix then.
 *
 * Save opens the post-capture summary (FieldCaptureSummary.js, 2026-10-16):
 * a dialog over the viewfinder with the AI's answer -- the top name and its
 * match, the alternatives to choose from -- and what was recorded. The batch
 * is held back from the outbox while it is open (EDIT_HOLD_MS); Done gives it
 * the camera's minute from then and sends it, Edit details hands it to the
 * batch editor. With the person's "ask automatically" switch on (the
 * default, utils/fieldAutoAi.js) the AI is asked as the summary opens, and
 * the cloud's model is woken on the first photo kept (POST /ai/warm), which
 * also says when no answer would come (dev and staging). A 429 (the daily
 * limit) stops asking by itself for the rest of this visit. The summary is a
 * dialog, not a fifth button, and has nothing to type.
 *
 * THE ENCIRCLE (phase A3; docs/FIELD_AI_VISION_SPEC.md section 13): a loop
 * drawn around a mushroom on the picture, for whoever may ask the AI, freezes
 * the frame there (utils/fieldFrame.js grabEncircle), keeps the loop drawn
 * where it was, and asks the AI about what it encloses at once
 * (components/ai/LiveIdentifyPanel.js: the top 5 with their match and a
 * photograph from the bank). A rare find at High certainty shows the B6
 * prompt in the panel; the request is kept with the specimen being
 * photographed -- the frozen frame its first photo if it has none -- and
 * sent by the outbox once the record is. "Keep this frame" files the frame
 * as the next view. Close (or Escape) is the live camera again. The Pro
 * drawer's Identify quick action is the same without drawing, about the
 * middle of the picture, and says the loop is there too ("Circle it";
 * WCAG 2.5.1). No new loop, sheet or swipe while the panel is open.
 */
export default function FieldCamera({ store, outbox, userId, user, onLeave }) {
  const { t } = useTranslation(['field', 'viewAs', 'handoffs']);
  const { t: tc } = useTranslation('camera');
  const { t: tai } = useTranslation('ai');
  const navigate = useNavigate();
  const reduced = usePrefersReducedMotion();
  const dir = useHtmlDir();
  const videoRef = useRef(null);
  const fileRef = useRef(null);
  const headingRef = useRef(null);
  const snapRef = useRef(null);
  const camera = useCameraStream(videoRef);
  const controls = useCameraControls(videoRef, camera.state === 'live');
  const tapFocus = useTapFocus(controls, { fit: 'cover', areaRef: videoRef });
  const modes = useCaptureMode(controls, { zstack: true });
  const zstack = useZStack(videoRef, controls);
  // The on-device detector's trial (docs/DEVICE_DETECTOR.md): nothing at all until the app ships a model.
  const [detectorOn, setDetectorOn] = useState(false);
  const detector = useDeviceDetector(videoRef, detectorOn && camera.state === 'live');
  // The sheet open over the camera, from the address. The controls sheet
  // only once the camera has said what it lets a page set; messages only for
  // whoever has chat.
  const { sheet, values: sheetValues, open: openSheet, close: closeSheet } = useFieldSheet();
  const canChat = can(user, 'chat:read');
  const proReady = camera.state === 'live' && !!controls.caps;
  // Tasks (plan phase 6): for whoever plays missions or takes part in events.
  const mayTasks = mayHaveTasks(user);
  let shown = sheet;
  if ((sheet === 'pro' && !proReady) || (sheet === 'hub' && !canChat) || (sheet === 'tasks' && !mayTasks)) shown = null;
  const [hubChannel, setHubChannel] = useState('');
  // The controls sheet rests just above the bar, so the shutter stays in
  // reach while the focus is set: the bar's height, followed while it is
  // open (the photo tray comes and goes inside the bar).
  const bottomRef = useRef(null);
  const [barHeight, setBarHeight] = useState(0);
  // The circled area's AI panel (the encircle): {loop, viewport, side, still, stillUrl, crop, takenAt, kept}.
  const [encircled, setEncircled] = useState(null);
  const encircledRef = useRef(null);
  // L16: what's left to ask once the panel has closed ({result}), and answers given before the specimen had a batch.
  const [traitLook, setTraitLook] = useState(null);
  const [heldTraits, setHeldTraits] = useState(null);
  const heldTraitsRef = useRef(null);
  const keepHeldTraits = (answers) => {
    heldTraitsRef.current = answers;
    setHeldTraits(answers);
  };
  // The Pro drawer's Annotate dialog (FieldAnnotate) is open.
  const [annotating, setAnnotating] = useState(false);
  useEffect(() => {
    if (shown !== 'pro' && !encircled) return undefined;
    const box = bottomRef.current;
    const measure = () => setBarHeight(box?.offsetHeight || 0);
    measure();
    const watch = typeof ResizeObserver !== 'undefined' && box ? new ResizeObserver(measure) : null;
    watch?.observe(box);
    window.addEventListener('resize', measure);
    return () => { watch?.disconnect(); window.removeEventListener('resize', measure); };
  }, [shown, encircled]);
  // Focus and light: the meter runs only while its sheet is open; what is
  // set stays set for every snap until reset.
  const [meter, resetMeter] = useFocusMeter(videoRef, camera.state === 'live' && shown === 'pro' && !annotating);
  // Messages waiting, for the messages handle's dot. Not asked with an
  // expired session: a 401 signs the person out (the outbox's rule).
  const unread = useChatUnread(user, { enabled: canChat && outbox.signedIn?.() !== false });
  // The Tasks chip's count, asked once here (the sheet asks again as it opens).
  const tasks = useFieldTasks(user, { enabled: mayTasks && outbox.signedIn?.() !== false });
  const adjusted = controls.adjusted;
  // One watch for Field Mode, shared with a live event's view (FieldGeoProvider).
  const { fix, fixRef, state: gpsState } = useFieldGeo();
  // Out on a live field event: its heartbeat, for the team's mini-map.
  const eventLive = useEventLive();
  // The field mission this phone is out on, if any: every new batch is
  // stamped with it (utils/activeMission.js), and a chip says so.
  const mission = useActiveMission(userId);
  const missionRef = useRef(mission);
  useEffect(() => { missionRef.current = mission; }, [mission]);
  // The observation this phone is walking to, if any (the national map's
  // target): new batches are stamped with it, and a small map shows the way.
  const target = useActiveTarget(userId);
  const targetRef = useRef(target);
  useEffect(() => { targetRef.current = target; }, [target]);
  const [targetOpen, setTargetOpen] = useState(true);
  // The way to the target along roads and trails, from the camera's one GPS
  // watch (utils/routing/useRoute): drawn on the overlay's map.
  const [travelMode, setTravelMode] = useTravelMode();
  const routeState = useRoute({ target, fix, mode: travelMode, enabled: !!target });
  const way = towards(fix, target);
  // Collection or Observation (utils/fieldMode.js): what a new batch starts
  // as. Whoever may not mint a specimen is always in Observation mode.
  const canCollect = can(user, 'specimen:register');
  const [fieldMode, setFieldMode] = useFieldMode(userId, canCollect);
  const modeRef = useRef(fieldMode);
  useEffect(() => { modeRef.current = fieldMode; }, [fieldMode]);
  const [modeOpen, setModeOpen] = useState(false);
  const stamp = () => ({ missionId: missionRef.current?.missionId || null,
    targetId: targetRef.current?.targetId || null, observeOnly: modeRef.current === OBSERVATION });
  // The way the back camera faces, for each photograph (0090). iOS asks the
  // person first, from a tap: the first Snap asks.
  const bearing = useDeviceHeading(camera.state === 'live');

  const [batch, setBatch] = useState(null);
  const batchRef = useRef(null);
  const [previews, setPreviews] = useState({});
  const previewsRef = useRef({});
  const [view, setView] = useState(MAIN_VIEWS[0]);
  const viewRef = useRef(MAIN_VIEWS[0]);
  const [counts, setCounts] = useState({ today: 0, waiting: 0, needFix: 0 });
  const [toast, setToast] = useState(null);
  // Field notes for the specimen being photographed: a sheet over the
  // viewfinder, opened on demand (nothing to type is on the camera itself),
  // saved into the batch's details and sent with the collection.
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesDraft, setNotesDraft] = useState('');
  const [announcement, setAnnouncement] = useState('');
  // A photo that could not be kept: on screen until the next one is.
  const [storeError, setStoreError] = useState(false);
  const [persistent, setPersistent] = useState(store.persistent !== false);
  const [flashing, setFlashing] = useState(false);
  // The specimen just saved, while its summary is open: {id, batch}.
  const [summary, setSummary] = useState(null);
  const summaryRef = useRef(null);
  summaryRef.current = summary;
  // Edit details: the batch editor holds the batch now, not the summary.
  const handedOff = useRef(false);
  const releaseRef = useRef(null);
  const [autoAi, setAutoAi] = useFieldAutoAi(userId);
  // What POST /ai/warm said (none in dev and staging), and whether the daily
  // limit stopped the asking for this visit.
  const [aiEnv, setAiEnv] = useState(null);
  // A warm-up asked and not yet answered: the summary waits for it before it
  // uploads anything (dev and staging answer prod_only).
  const [aiEnvPending, setAiEnvPending] = useState(false);
  const [aiPaused, setAiPaused] = useState(false);
  const warmed = useRef(false);
  const warming = useRef(false);   // a warm-up sent and not yet answered
  const warmCap = useRef(null);
  const autoAiRef = useRef(autoAi);
  autoAiRef.current = autoAi;
  const mounted = useRef(true);
  // The latest `t`, for work that outlives the render it was asked in.
  const tRef = useRef(t);
  useEffect(() => { tRef.current = t; });
  // Said aloud through the region that is always there (one inserted with
  // its text is often not read). The same words twice differ by a trailing
  // no-break space, so the second time is a change, and is read too.
  const say = useCallback((words) => {
    setAnnouncement(prev => (prev === words ? `${words}\u00a0` : words));
  }, []);
  // Store work in the order it was asked for: a second snap must not build
  // on a batch the first has not finished writing.
  const chain = useRef(Promise.resolve());
  const enqueue = useCallback((work) => {
    chain.current = chain.current.then(work).catch((e) => {
      if (!mounted.current) return;
      // Refused because an admin is viewing the app as a role: nothing is
      // kept, and that is not the device's fault (utils/viewAs.js).
      const readOnly = isReadOnlyRefusal(e);
      setStoreError(readOnly ? 'readOnly' : true);
      say(tRef.current(readOnly ? 'viewAs:notKept' : 'camera.storeFailed'));
      navigator.vibrate?.([80, 60, 80]);
    });
    return chain.current;
  }, [say]);

  const chooseView = (next) => {
    viewRef.current = next;
    setView(next);
  };
  const showBatch = (next) => {
    batchRef.current = next;
    setBatch(next);
  };
  const forget = (ids) => {
    for (const id of ids) {
      if (previewsRef.current[id]) URL.revokeObjectURL(previewsRef.current[id]);
      delete previewsRef.current[id];
    }
    setPreviews({ ...previewsRef.current });
  };

  // Whether this browser keeps anything at all is known once the store has
  // tried to open.
  useEffect(() => {
    let alive = true;
    store.ready?.().then((kept) => { if (alive) setPersistent(kept !== false); }).catch(() => {});
    return () => { alive = false; };
  }, [store]);

  useEffect(() => {
    mounted.current = true;
    // Unless a sheet the address opened already holds it (a link, an editor's Back).
    if (!document.activeElement?.closest?.('[role="dialog"]')) headingRef.current?.focus?.();
    return () => {
      mounted.current = false;
      Object.values(previewsRef.current).forEach(url => URL.revokeObjectURL(url));
      previewsRef.current = {};
      if (encircledRef.current?.stillUrl) URL.revokeObjectURL(encircledRef.current.stillUrl);
      // Left with a summary open (the phone's Back, another screen): the
      // specimen goes as Done would send it, unless the editor took it over.
      if (summaryRef.current && !handedOff.current) releaseRef.current?.(summaryRef.current.id);
    };
  }, []);

  // The batch left unfinished last time, photos and all.
  useEffect(() => {
    let cancelled = false;
    enqueue(async () => {
      const active = await store.active(userId);
      if (cancelled || !active) return;
      for (const p of active.photos || []) {
        const blob = await store.getBlob(userId, p.id);
        if (blob && !cancelled) previewsRef.current[p.id] = URL.createObjectURL(blob);
      }
      if (cancelled) return;
      setPreviews({ ...previewsRef.current });
      showBatch(active);
      chooseView(nextView(active.photos));
    });
    return () => { cancelled = true; };
  }, [store, userId, enqueue]);

  // Today's specimens and those still to send, for the Gallery's badge; and
  // whether any is waiting for a place this screen can give it.
  useEffect(() => {
    let alive = true;
    const recount = async () => {
      const all = (await store.list(userId)).filter(b => b.status !== 'active');
      if (!alive) return;
      setCounts({
        today: all.filter(b => isToday(b.startedAt || b.createdAt)).length,
        waiting: all.filter(b => b.status === 'pending' || b.status === 'sending').length,
        needFix: all.filter(b => b.status === 'pending' && !b.location && b.source === 'camera').length,
      });
    };
    recount().catch(() => {});
    const off = store.subscribe(() => { recount().catch(() => {}); });
    return () => { alive = false; off(); };
  }, [store, userId]);

  useEffect(() => {
    if (counts.needFix && fix) outbox.offerFix(fix);
  }, [fix, counts.needFix, outbox]);

  // The chip changes with every fix; only finding or losing the location is
  // said aloud, or a screen reader would read out each new accuracy.
  useEffect(() => {
    if (gpsState === 'ok') setAnnouncement(tRef.current('camera.gpsOkNoAccuracy'));
    else if (gpsState === 'none') setAnnouncement(tRef.current('camera.gpsNone'));
  }, [gpsState]);

  // Reaching the target is said once, not the distance with every step.
  const arrived = !!way?.arrived;
  useEffect(() => {
    if (arrived) setAnnouncement(tRef.current('camera.targetArrived'));
  }, [arrived]);

  useEffect(() => {
    if (!toast) return undefined;
    const id = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(id);
  }, [toast]);

  useEffect(() => {
    if (!flashing) return undefined;
    const id = setTimeout(() => setFlashing(false), 150);
    return () => clearTimeout(id);
  }, [flashing]);

  const openNotes = () => {
    setNotesDraft(batchRef.current?.details?.general_notes || '');
    setNotesOpen(true);
  };
  const openControls = () => {
    setNotesOpen(false);
    openSheet('pro');
  };
  const changeMode = (mode) => {
    setFieldMode(mode);
    say(tRef.current('mode.changed', { mode: tRef.current(`mode.${mode}`) }));
  };
  // "Apply to this batch too": the specimen being photographed changes with
  // the mode; otherwise the change waits for the next one.
  const applyModeToBatch = (mode) => enqueue(async () => {
    const current = batchRef.current;
    if (!current) return;
    const details = { ...(current.details || {}) };
    if (mode === OBSERVATION) details.observeOnly = true;
    else delete details.observeOnly;
    const next = await store.save({ ...current, details });
    if (mounted.current) showBatch(next);
  });
  const saveNotes = () => {
    const text = notesDraft.trim();
    setNotesOpen(false);
    return enqueue(async () => {
      const current = batchRef.current || newBatch({ userId, source: 'camera', ...stamp() });
      const details = { ...(current.details || {}) };
      if (text) details.general_notes = text;
      else delete details.general_notes;
      const next = await store.save({ ...current, details });
      if (!mounted.current) return;
      showBatch(next);
      say(tRef.current('camera.notesSaved'));
    });
  };

  // The cloud's model loads while the specimen is still being photographed (a
  // cold one takes longer than an identification waits), and its answer says
  // whether one will come at all. Once per visit, and only when the summary
  // is to ask by itself (or the person just turned that on: `force`): from
  // the first photo kept, from Save, from the switch, and when the connection
  // comes back with a summary open. Not waited for long: a warm-up that has
  // not answered in WARM_WAIT_MS lets the summary ask, as a failed one does;
  // one still out when Save is pressed (a slow link, the first photo long
  // ago) is waited for again, WARM_WAIT_MS from then.
  const holdForWarmUp = () => {
    setAiEnvPending(true);
    clearTimeout(warmCap.current);
    warmCap.current = setTimeout(() => { if (mounted.current) setAiEnvPending(false); }, WARM_WAIT_MS);
  };
  const wake = ({ force = false } = {}) => {
    if (warming.current) { holdForWarmUp(); return; }
    if (warmed.current || (!force && !autoAiRef.current) || !mayRequestAi(user) || !outbox.signedIn?.()) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    warmed.current = true;
    warming.current = true;
    holdForWarmUp();
    warmAi(KEEP)
      .then((env) => { if (mounted.current && env) setAiEnv(env); })
      .finally(() => {
        warming.current = false;
        clearTimeout(warmCap.current);
        if (mounted.current) setAiEnvPending(false);
      });
  };
  const wakeRef = useRef(wake);
  wakeRef.current = wake;

  // `fix` where the photo says where it was taken; else the phone's own, if
  // it is fresh at the moment of the photo. `view` and `note` file it other
  // than as the next view (a bracket's extras); `quiet` says nothing aloud.
  const addPhoto = async (blob, takenAt, fix, { view = null, note = null, quiet = false } = {}) => {
    const current = batchRef.current || newBatch({ userId, source: 'camera', now: takenAt, ...stamp() });
    const chosen = view || viewRef.current;
    const count = current.photos.length + 1;
    const photo = newBatchPhoto({
      view: chosen,
      takenAt,
      fix: fix === undefined ? freshFix(fixRef.current, takenAt.getTime()) : fix,
      // A picture chosen from storage was not taken facing anything now.
      heading: fix === undefined ? headingForPhoto(bearing.read(takenAt.getTime())) : null,
      // An "other" photo says what it is in words (the wizard's rule); here
      // there is nobody to type them, so it is numbered.
      note: note || (chosen === 'other' ? t('camera.extraNote', { n: count }) : undefined),
    });
    await store.putBlob(userId, photo.id, blob);
    const next = await store.save({ ...withTraitAnswers(current, heldTraitsRef.current), photos: [...current.photos, photo] });
    keepHeldTraits(null);
    setStoreError(false);
    // Photos that exist nowhere else: ask the browser not to clear them.
    store.ensureDurable?.().catch(() => {});
    wake();
    showBatch(next);
    if (mounted.current) {
      previewsRef.current[photo.id] = URL.createObjectURL(blob);
      setPreviews({ ...previewsRef.current });
    }
    chooseView(nextView(next.photos));
    if (!quiet) say(t('camera.snapped', { view: t(`views.${chosen}`), n: count }));
    return photo.id;
  };

  // A kept Z-stack: its sharpest layer filed under the view, the others as
  // extras that say which layer they are; the batch keeps which photographs
  // make the stack, for the outbox's Bundles step (utils/fieldOutbox.js).
  const addStack = stack => enqueue(async () => {
    const takenAt = stack.startedAt || new Date();
    const main = viewRef.current;
    const count = stack.layers.length;
    const ids = new Array(count);
    ids[stack.cover] = await addPhoto(stack.layers[stack.cover].blob, takenAt, undefined, { quiet: true });
    for (let i = 0; i < count; i += 1) {
      if (i === stack.cover) continue;
      ids[i] = await addPhoto(stack.layers[i].blob, takenAt, undefined, {
        view: 'other', quiet: true, note: tc('zstack.note', { n: i + 1, count, view: t(`views.${main}`) }),
      });
    }
    const current = batchRef.current;
    const next = await store.save({
      ...current, bundles: [...(current.bundles || []), newBatchBundle(stack, ids, { device: zstack.device() })],
    });
    if (!mounted.current) return;
    showBatch(next);
    say(tc('zstack.saved', { count }));
  });

  // Taken the moment the shutter is pressed: the click, the flash and the
  // buzz at once, whatever the store is still doing.
  const shutter = () => {
    if (!reduced) setFlashing(true);
    navigator.vibrate?.(30);
  };

  const snap = () => {
    // The live AI panel open over a frozen frame: the shutter is the live camera again first.
    if (encircledRef.current?.stillUrl) closeEncircled({ focus: false });
    if (camera.state === 'unavailable') {
      fileRef.current?.click();
      return;
    }
    // A camera still starting is waited for (grabFrame gives it a moment):
    // a tap in the second it takes to start is a photo, not a refusal.
    if (camera.state !== 'live' && camera.state !== 'starting') {
      setToast({ text: t('camera.notReady'), at: Date.now() });
      return;
    }
    // A burst, a bracket or a Z-stack still being taken: this press is not a photo.
    if (modes.busy || zstack.active) return;
    if (modes.mode === 'zstack' && camera.state === 'live') {
      // The sheet steps aside: the picture is the subject, and the bar above
      // the shutter says how far the stack is.
      if (shown === 'pro') closeSheet();
      navigator.vibrate?.(30);
      zstack.start();
      return;
    }
    const takenAt = new Date();
    shutter();
    // iOS gives the compass only after asking, and only from a tap.
    if (bearing.state === 'idle') bearing.request();
    // The frame is grabbed now, not when the store gets to it: two quick
    // snaps are two moments, not the same one twice. The capture mode
    // (CaptureModes) only once the camera is live; a camera still starting
    // takes one frame.
    const grabbing = camera.state === 'live'
      ? modes.run(videoRef.current, { gain: controls.gain })
      : grabFrame(videoRef.current, { gain: controls.gain }).then(blob => ({ mode: 'single', shots: [{ blob }] }));
    grabbing.catch(() => {});
    enqueue(async () => {
      let taken;
      try {
        taken = await grabbing;
      } catch (e) {
        if (e?.message !== 'capture-busy') setToast({ text: t('camera.notReady'), at: Date.now() });
        return;
      }
      if (taken.mode !== 'bracket') {
        await addPhoto(taken.shots[0].blob, takenAt);
        return;
      }
      // A bracket: the photo as set under the view, the darker and the
      // brighter as extras that say what they are.
      const main = viewRef.current;
      const asSet = taken.shots.find(shot => shot.ev === 0) || taken.shots[0];
      await addPhoto(asSet.blob, takenAt);
      for (const shot of taken.shots.filter(other => other !== asSet)) {
        await addPhoto(shot.blob, takenAt, undefined, {
          view: 'other', quiet: true,
          note: tc('capture.bracketNote', { ev: evLabel(shot.ev), view: t(`views.${main}`) }),
        });
      }
    });
  };

  const onFile = (event) => {
    const file = event.target.files && event.target.files[0];
    // Reset so the same picture chosen twice still fires.
    event.target.value = '';
    if (!file) return;
    const picked = new Date();
    enqueue(async () => {
      // The phone's camera app may have written when and where into the
      // file. An EXIF time can be hours old, and the phone's fix now is no
      // place for it: that fix goes only with a photo taken within its age
      // of it, either side (freshFix alone would take any older moment).
      const meta = await exifFromFile(file);
      const takenAt = meta?.takenAt || picked;
      const here = fixRef.current;
      const near = here && Math.abs(takenAt.getTime() - Number(here.at)) <= FIX_MAX_AGE_MS;
      await addPhoto(file, takenAt, meta?.gps || (near ? freshFix(here, takenAt.getTime()) : null));
    });
  };

  const removePhoto = (id) => enqueue(async () => {
    const current = batchRef.current;
    if (!current) return;
    const photos = current.photos.filter(p => p.id !== id);
    await store.removeBlobs(userId, [id]);
    // A thermal file the drawer added before thermal left the camera
    // (2026-10-26) keeps the specimen. A Z-stack's layer goes from its stack
    // too, and the photo's marks (Annotate) with it.
    if (photos.length || current.thermal?.length) {
      showBatch(await store.save({ ...current, photos, bundles: dropFromBundles(current.bundles, id),
        ...keepAnnotationsFor(current, photos.map(p => p.id)) }));
    } else {
      await store.remove(userId, current.id);
      showBatch(null);
    }
    forget([id]);
    chooseView(nextView(photos));
    say(t('camera.removed'));
    // The button went with its photo; the shutter is where the hand goes next.
    snapRef.current?.focus?.();
  });

  const finish = () => {
    if (encircledRef.current?.stillUrl) closeEncircled({ focus: false });
    setTraitLook(null);
    if (!batchRef.current?.photos?.length) {
      // Not a silent no: a tap on it before any photo says what it needs.
      const words = t('camera.needPhoto');
      setToast({ text: words, at: Date.now() });
      say(words);
      return undefined;
    }
    return enqueue(async () => {
      const current = batchRef.current;
      if (!current?.photos?.length) return;
      const cover = coverPhoto(current);
      const blob = cover ? await store.getBlob(userId, cover.id) : null;
      const thumbnail = blob ? await thumbnailOf(blob) : null;
      const finishedAt = new Date();
      const done = {
        ...finalizeBatch({ ...current, thumbnail }, fixRef.current, finishedAt),
        // Held while its summary is open, written before the save: the outbox
        // must not mint it in between (Done gives it the camera's minute).
        holdUntil: new Date(finishedAt.getTime() + EDIT_HOLD_MS).toISOString(),
      };
      await store.save(done);
      showBatch(null);
      forget(current.photos.map(p => p.id));
      chooseView(MAIN_VIEWS[0]);
      if (!mounted.current) {
        releaseHold(done.id);
        return;
      }
      handedOff.current = false;
      wake();
      setSummary({ id: done.id, batch: done });
    });
  };

  // The camera's minute, counted from Done (the toast promises it): a label
  // or "only a sighting" can still be given from the gallery. A batch with no
  // place yet takes the phone's fix now, if fresh: the collector reviewing
  // the summary is still beside the specimen. Answers the stored batch.
  const releaseHold = (id) => {
    const hold = outbox.cameraHoldMs ?? CAMERA_HOLD_MS;
    const until = hold > 0 ? new Date(Date.now() + hold).toISOString() : undefined;
    const here = freshFix(fixRef.current, Date.now());
    return store.update(userId, id, (current) => {
      if (current.status !== 'pending' || current.serverSampleId) return null;
      return {
        ...current,
        holdUntil: until,
        ...(!current.location && here ? {
          location: { lat: here.lat, lng: here.lng, accuracy_m: Number.isFinite(here.accuracy_m) ? here.accuracy_m : null },
        } : {}),
      };
    // The run is started, never waited for: Done must not wait for every
    // other specimen's upload.
    }).catch(() => null).finally(() => { outbox.run(); });
  };
  releaseRef.current = releaseHold;

  const closeSummary = async () => {
    const open = summaryRef.current;
    if (!open) return;
    const kept = await releaseHold(open.id);
    if (!mounted.current) return;
    setSummary(null);
    let message = 'camera.savedSending';
    // Gone out while the summary was open: "in a minute" would be wrong.
    if (kept && (kept.status !== 'pending' || kept.serverSampleId)) message = 'summary.alreadySent';
    else if (!kept?.location) message = 'camera.savedNoLocation';
    else if (typeof navigator !== 'undefined' && navigator.onLine === false) message = 'camera.savedOffline';
    setToast({ text: t(message), at: Date.now() });
    say(t(message));
  };

  // The batch editor holds the batch from here (FieldBatchEditor's own hold,
  // let go when it closes); Back there returns to the camera.
  const editSummary = () => {
    const open = summaryRef.current;
    if (!open) return;
    handedOff.current = true;
    navigate(`/field/batch/${open.id}`, { state: { fromCamera: true } });
  };

  // The encircle: the frame frozen where the loop closed, the loop kept
  // drawn, and the AI asked about what it encloses (LiveIdentifyPanel).
  const mayEncircle = mayRequestAi(user) && camera.state === 'live';
  const showEncircled = (next) => {
    encircledRef.current = next;
    setEncircled(next);
  };
  const encircle = async (loop, viewport) => {
    if (encircledRef.current || zstack.active || modes.busy || !mayEncircle) return;
    setTraitLook(null);
    const bounds = loopBounds(loop);
    const takenAt = new Date();
    // Held while the frame is grabbed: a second loop drawn meanwhile is not a second panel.
    encircledRef.current = { pending: true };
    let grabbed;
    try {
      grabbed = await grabEncircle(videoRef.current, { bounds, viewport }, { gain: controls.gain });
    } catch {
      encircledRef.current = null;
      if (mounted.current) setToast({ text: t('camera.notReady'), at: Date.now() });
      return;
    }
    if (!mounted.current) { encircledRef.current = null; return; }
    navigator.vibrate?.(20);
    showEncircled({
      loop, viewport, side: panelSide(bounds, viewport), still: grabbed.still, crop: grabbed.crop, takenAt,
      stillUrl: URL.createObjectURL(grabbed.still), kept: false,
      traitFraming: baseMayBeOutOfView(bounds, viewport, { bottomInset: barHeight }),
    });
    say(tai('live.starting'));
    // The cloud's model woken (and dev's prod-only learnt) before the panel asks.
    wake({ force: true });
  };
  // The Pro drawer's Identify quick action, for whoever cannot draw a loop:
  // the middle of the picture.
  const identifyCentre = () => {
    const rect = videoRef.current?.getBoundingClientRect?.();
    const viewport = { width: rect?.width || window.innerWidth, height: rect?.height || window.innerHeight };
    closeSheet();
    encircle(centreLoop(viewport), viewport);
  };
  // The Pro drawer's Annotate quick action (FieldAnnotate): the marks are
  // written to the batch in the camera's own order of store work, so a snap
  // meanwhile cannot write over them; a failure is the dialog's to say.
  const mayMark = mayAnnotate(user);
  const saveAnnotations = (changes) => {
    const work = chain.current.then(async () => {
      const current = batchRef.current;
      if (!current) throw new Error('no specimen to annotate');
      const next = await store.save(withAnnotations(current, changes));
      if (mounted.current) {
        showBatch(next);
        const count = Object.keys(next.annotations || {}).length;
        say(count ? tRef.current('annotate.saved', { count }) : tRef.current('annotate.removed'));
      }
      return next;
    });
    chain.current = work.catch(() => {});
    return work;
  };
  // Closed, the focus goes back to where it came from (ui/Dialog). Where it
  // came from nowhere (a tap that focuses nothing, as on iOS) or from what is
  // gone (the drawer, closed by the phone's Back): the Annotate button, else
  // the shutter.
  const annotateRef = useRef(null);
  const hadAnnotate = useRef(false);
  // A specimen with no photo left has nothing to annotate: the dialog does not wait to open again.
  const photoCount = batch?.photos?.length || 0;
  useEffect(() => { if (!photoCount) setAnnotating(false); }, [photoCount]);
  useEffect(() => {
    if (annotating) hadAnnotate.current = true;
    else if (hadAnnotate.current) {
      hadAnnotate.current = false;
      if (!document.activeElement || document.activeElement === document.body) {
        (annotateRef.current?.isConnected ? annotateRef : snapRef).current?.focus?.();
      }
    }
  }, [annotating]);
  const closeEncircled = ({ focus = true } = {}) => {
    const open = encircledRef.current;
    if (open?.stillUrl) URL.revokeObjectURL(open.stillUrl);
    showEncircled(null);
    // What the panel asked and nobody has answered stays on the live camera, for going to look.
    if (open?.traitResult) setTraitLook({ result: open.traitResult });
    if (focus) snapRef.current?.focus?.();
  };
  // L16: the AI's answer is held for the checks, and a person's answer goes on the batch for the outbox's phenotype
  // step, or waits for the specimen's first photo. It never goes near the AI's answer (utils/ai/fieldTraits.js).
  const heardLive = useCallback((result) => {
    const open = encircledRef.current;
    if (!open?.stillUrl) return;
    encircledRef.current = { ...open, traitResult: result };
    setEncircled(encircledRef.current);
  }, []);
  const answerTrait = (trait, answer) => enqueue(async () => {
    // Over the live camera the question moves on at once, so what the answer doesn't mean is said here.
    if (answer === 'no' && !encircledRef.current) setToast({ text: tai('traits.noClears'), at: Date.now() });
    const current = batchRef.current;
    if (!current) {
      keepHeldTraits({ ...heldTraitsRef.current, [trait]: answer });
      return;
    }
    showBatch(await store.save(withTraitAnswer(current, trait, answer)));
  });
  const shownBatchId = useRef(null);
  useEffect(() => {
    const id = batch?.id || null;
    // Another specimen: what was asked about the last one isn't asked about this one.
    if (shownBatchId.current && shownBatchId.current !== id) {
      setTraitLook(null);
      keepHeldTraits(null);
    }
    shownBatchId.current = id;
  }, [batch?.id]);
  const traitAnswers = batch?.traitChecks || heldTraits;
  const mayRecordAnswers = mayRecordTraits(user);
  // The frozen frame, as the next view of the specimen being photographed (once).
  const keepFrame = () => enqueue(async () => {
    const open = encircledRef.current;
    if (!open?.still || open.kept) return;
    await addPhoto(open.still, open.takenAt);
    if (mounted.current && encircledRef.current === open) showEncircled({ ...open, kept: true });
  });
  // A rare find's pickup or drop-off (B6), kept with the specimen being
  // photographed -- the frozen frame its first photo if it has none -- and
  // sent by the outbox once the record is; none takes it back.
  const withFrame = async () => {
    if (batchRef.current?.photos?.length) return;
    const open = encircledRef.current;
    if (!open?.still) return;
    await addPhoto(open.still, open.takenAt, undefined, { quiet: true });
    if (mounted.current && encircledRef.current === open) showEncircled({ ...open, kept: true });
  };
  const keepLiveHandoff = (request, aiId) => enqueue(async () => {
    if (request) await withFrame();
    const current = batchRef.current;
    if (!current) return;
    const next = withHandoffRequest(current, request, { aiId });
    if (!next) return;
    showBatch(await store.save(next));
    say(t(request ? 'handoffs:prompt.saved' : 'handoffs:prompt.removed'));
  });
  // "Collect it as a specimen": this batch is sent as a collection, so it is minted a code.
  const collectLive = () => enqueue(async () => {
    await withFrame();
    const current = batchRef.current;
    if (!current) return;
    const details = { ...(current.details || {}) };
    delete details.observeOnly;
    showBatch(await store.save({ ...current, details }));
    say(t('handoffs:prompt.collecting'));
  });
  const observing = batch ? !!batch.details?.observeOnly : fieldMode === OBSERVATION;
  const here = freshFix(fix, Date.now());

  // The connection back with a summary open: the warm-up a photo taken
  // offline could not send goes now, before the summary asks.
  useEffect(() => {
    if (!summary) return undefined;
    const back = () => wakeRef.current();
    window.addEventListener('online', back);
    return () => window.removeEventListener('online', back);
  }, [summary]);

  // The summary closed: the shutter is where the hand goes next.
  const hadSummary = useRef(false);
  useEffect(() => {
    if (summary) hadSummary.current = true;
    else if (hadSummary.current) {
      hadSummary.current = false;
      snapRef.current?.focus?.();
    }
  }, [summary]);

  const photos = batch?.photos || [];
  // The photos marked with Annotate, for its quick action's label.
  const marked = Object.keys(batch?.annotations || {}).length;
  let gpsText = t('camera.gpsLocating');
  if (gpsState === 'ok') {
    gpsText = Number.isFinite(fix.accuracy_m)
      ? t('camera.gpsOk', { m: Math.round(fix.accuracy_m) })
      : t('camera.gpsOkNoAccuracy');
  } else if (gpsState === 'none') {
    gpsText = t('camera.gpsNone');
  }
  const viewName = t(`views.${view}`);
  // The edges a swipe opens a sheet from, and when: never over another sheet,
  // the summary, the mode dialog or the notes being typed.
  const edges = ['right', ...(canChat ? ['left'] : []), ...(proReady ? ['bottom'] : [])];
  // Nor while the Annotate dialog is open: a stroke there is a mark.
  const gestures = !shown && !summary && !modeOpen && !notesOpen && !zstack.active && !encircled && !annotating;
  // Two fingers zoom the camera with the controls sheet open too: it covers
  // none of the picture.
  const pinching = (!shown || shown === 'pro') && !summary && !modeOpen && !notesOpen && !encircled
    && !annotating && camera.state === 'live';
  const messagesLabel = unread > 0 ? t('sheet.openMessagesNew') : t('sheet.openMessages');

  return (
    <CameraViewport className="fm-screen" style={cs.root} data-testid="field-camera" data-camera={camera.state}
      data-sheet={shown || ''} edges={edges} enabled={gestures} pinch={pinching} onPinch={controls.onPinch}
      lasso={mayEncircle} onLasso={encircle} data-encircled={encircled ? 'true' : undefined}
      onEdgeSwipe={(edge) => { setNotesOpen(false); openSheet(SHEET_AT_EDGE[edge]); }}>
      <style>{FOCUS_CSS + LANDSCAPE_CSS}</style>
      {/* A tap on the picture focuses and meters the light there, where the camera can; the ring says so.
          The top band lets a tap through to it, but for its chips. */}
      <video ref={videoRef} style={{ ...cs.video, filter: controls.previewFilter }} playsInline muted autoPlay
        aria-hidden="true" data-testid="field-video" onClick={tapFocus.onTap} />
      <DetectorBoxes boxes={detector.boxes} videoRef={videoRef} />
      <FocusRing ring={tapFocus.ring} testIdPrefix="field-camera" />
      {flashing && <div style={cs.flash} aria-hidden="true" data-testid="field-flash" />}
      {/* The encircle: the frame frozen where the loop closed, and the loop
          drawn where it was, under the AI's panel. */}
      {encircled?.stillUrl && (
        <>
          <img src={encircled.stillUrl} alt="" aria-hidden="true" style={cs.still} data-testid="field-encircle-still" />
          <svg style={cs.loopLayer} viewBox={`0 0 ${encircled.viewport.width} ${encircled.viewport.height}`}
            preserveAspectRatio="none" role="img" aria-label={tai('live.loop')} data-testid="field-encircle-loop">
            <path d={loopPath(encircled.loop)} fill="rgba(255, 255, 255, 0.08)" stroke="rgba(0, 0, 0, 0.55)"
              strokeWidth={7} strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
            <path d={loopPath(encircled.loop)} fill="none" stroke="#fff" strokeWidth={3} strokeLinejoin="round"
              vectorEffect="non-scaling-stroke" />
          </svg>
          {encircled.traitFraming && fieldTraitAsks(encircled.traitResult).some(ask => ask.trait === 'volva') && (
            <FieldTraitGuide offset={barHeight} testId="field-traits-guide" />
          )}
        </>
      )}

      {/* The sheets' handles, on the physical edges they come from: the way
          in that always works, where the phone keeps the edge swipe for its
          own Back. Not buttons of the bar. */}
      <button type="button" style={{ ...cs.handle, right: 0 }} data-testid="field-handle-gallery"
        aria-label={t('sheet.openGallery')} title={t('sheet.openGallery')} aria-haspopup="dialog"
        onClick={() => { setNotesOpen(false); openSheet('gallery'); }}>
        <span style={{ ...cs.handleTab, borderRadius: '12px 0 0 12px' }} aria-hidden="true">▦</span>
      </button>
      {canChat && (
        <button type="button" style={{ ...cs.handle, left: 0 }} data-testid="field-handle-hub"
          aria-label={messagesLabel} title={messagesLabel} aria-haspopup="dialog" data-unread={unread}
          onClick={() => { setNotesOpen(false); openSheet('hub'); }}>
          <span style={{ ...cs.handleTab, borderRadius: '0 12px 12px 0' }} aria-hidden="true">
            <ChatIcon />
            {unread > 0 && <span style={cs.handleDot} data-testid="field-handle-hub-unread" />}
          </span>
        </button>
      )}

      <div style={cs.top}>
        <h1 ref={headingRef} tabIndex={-1} style={srOnly}>{t('camera.heading')}</h1>
        {/* The first row is what a short screen keeps, and the toast never
            covers it. The Map chip is outside the bar: not a fifth button.
            A push, so the phone's own Back returns to the camera. */}
        <div style={cs.topRow}>
          <span style={cs.chip} data-testid="field-gps" data-state={gpsState}>
            <span aria-hidden="true">📍 </span>{gpsText}
          </span>
          {/* The map is staff's and outsiders' own records (routes/map_points.py); a
              public account has none there. */}
          {!isPublicOnly(user) && (
            <button type="button" style={cs.mapChip} data-testid="field-map" title={t('tip.camera.map')}
              onClick={() => navigate('/map', { state: { fromField: true } })}>
              <span aria-hidden="true">🗺 </span>{t('camera.map')}
            </button>
          )}
          {mayTasks && (
            /* Tasks (n): missions assigned to me and mine, and my events
               (FieldTasksSheet.js). No swipe opens it; this chip does. */
            <button type="button" style={cs.mapChip} data-testid="field-tasks-chip"
              title={tasks.state === 'ready' ? t('tasks.waiting', { count: tasks.count }) : t('tasks.open')}
              aria-haspopup="dialog" aria-expanded={shown === 'tasks'} data-count={tasks.state === 'ready' ? tasks.count : ''}
              onClick={() => { setNotesOpen(false); openSheet('tasks'); }}>
              <span aria-hidden="true">📋 </span>
              {tasks.state === 'ready' ? t('tasks.chipCount', { count: tasks.count }) : t('tasks.chip')}
            </button>
          )}
          {/* Report issue: in the row a short screen keeps (in the second it
              was clipped away in landscape); the bottom bar keeps its four
              buttons. */}
          {can(user, 'issue:report') && (
            <ReportIssueButton style={cs.mapChip} testId="field-report-issue">
              <span aria-hidden="true">⚑</span>
            </ReportIssueButton>
          )}
        </div>
        <div style={{ ...cs.topRow, flexWrap: 'wrap' }}>
          {/* Field notes for this specimen: a dot says some are saved. */}
          <button type="button" style={cs.mapChip} data-testid="field-notes" title={t('camera.notesOpen')}
            aria-expanded={notesOpen} data-has-notes={!!batch?.details?.general_notes} onClick={openNotes}>
            <span aria-hidden="true">📝 </span>{t('camera.notes')}{batch?.details?.general_notes ? ' •' : ''}
          </button>
          {proReady && (
            /* Focus and light: the controls sheet. A dot says something is set by hand. */
            <button type="button" style={cs.mapChip} data-testid="field-adjust" title={tc('open')}
              aria-haspopup="dialog" aria-expanded={shown === 'pro'} data-adjusted={adjusted}
              onClick={openControls}>
              {tc('chip')}{adjusted ? ' •' : ''}
            </button>
          )}
          {mission && (
            /* The mission the next batch is for; the page it came from. */
            <button type="button" style={cs.mapChip} data-testid="field-mission" data-mission={mission.missionId}
              title={t('camera.missionOpen')}
              onClick={() => (mission.event
                ? navigate(`/field/event/${mission.missionId}`, { state: { fromField: true } })
                : openSheet('tasks', { mission: mission.missionId }))}>
              <span aria-hidden="true">🎯 </span>{t('camera.mission', { title: mission.title || '' })}
            </button>
          )}
          {target && (
            /* The observation being walked to: the chip shows or hides the
               map to it, its × forgets it. */
            <span style={cs.targetGroup} data-testid="field-target" data-target={target.targetId || ''}>
              <button type="button" style={cs.targetChip} data-testid="field-target-chip" title={t('tip.camera.target')}
                aria-expanded={targetOpen} aria-controls="field-target-map" onClick={() => setTargetOpen(o => !o)}>
                <span aria-hidden="true">📍 </span>
                <span style={cs.targetLabel}>{t('camera.target', { label: target.label || t('camera.targetUnnamed') })}</span>
                {way && <bdi data-testid="field-target-distance">{' · '}{distanceLabel(way.km, t)}</bdi>}
              </button>
              <button type="button" style={cs.targetClear} data-testid="field-target-clear" title={t('tip.camera.targetClear')}
                aria-label={t('camera.targetClear')} onClick={() => clearActiveTarget()}>
                <span aria-hidden="true">×</span>
              </button>
            </span>
          )}
        </div>
        {notesOpen && (
          <div style={cs.notesSheet} data-testid="field-notes-sheet" role="group" aria-label={t('camera.notesLabel')}>
            <label style={{ display: 'block', fontSize: 13, marginBottom: 4 }} htmlFor="field-notes-text">
              {t('camera.notesLabel')}
            </label>
            <textarea id="field-notes-text" data-testid="field-notes-text" value={notesDraft} rows={4}
              onChange={(e) => setNotesDraft(e.target.value)} placeholder={t('camera.notesHint')}
              style={cs.notesText} />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 6 }}>
              <button type="button" style={cs.mapChip} data-testid="field-notes-close" onClick={() => setNotesOpen(false)}>
                {t('camera.notesClose')}
              </button>
              <button type="button" style={{ ...cs.mapChip, background: 'var(--c-primary)' }} data-testid="field-notes-save"
                onClick={saveNotes}>
                {t('camera.notesSave')}
              </button>
            </div>
          </div>
        )}
        {camera.state === 'unavailable' && (
          <p style={cs.notice} data-testid="field-camera-notice" data-reason={camera.reason}>
            {t(`camera.unavailable.${camera.reason}`)}
          </p>
        )}
        {!persistent && <p style={cs.notice} data-testid="field-not-kept">{t('camera.notPersistent')}</p>}
        {storeError && (
          <p style={{ ...cs.notice, ...cs.alert }} role="alert" data-testid="field-store-error">
            {storeError === 'readOnly' ? t('viewAs:notKept') : t('camera.storeFailed')}
          </p>
        )}
      </div>

      <div ref={bottomRef} className="fm-cam-bottom" style={cs.bottom}>
        {/* The controls sheet's handle, a grip just above the bar: pulled up
            (or tapped) it opens what the "Focus & light" chip opens. */}
        {proReady && (
          <button type="button" style={cs.handleBottom} data-testid="field-handle-pro"
            aria-label={t('sheet.openControls')} title={t('sheet.openControls')} aria-haspopup="dialog"
            onClick={openControls}>
            <span style={cs.handleGrip} aria-hidden="true" />
          </button>
        )}
        {/* Over the viewfinder, not in the stack: it must never push the
            shutter off a short screen. Said aloud through field-live. Above
            the grip when there is one. */}
        {toast && (
          <p style={{ ...cs.toast, ...(proReady ? cs.toastOverGrip : null) }} aria-hidden="true"
            data-testid="field-toast">{toast.text}</p>
        )}
        {/* A Z-stack being taken: how far, with Stop (a sweep) and Cancel. */}
        {zstack.busy && (
          <div style={{ ...cs.zstackRun, ...(proReady ? cs.toastOverGrip : null) }}>
            <ZStackRun zstack={zstack} tone="dark" testIdPrefix="field-camera" />
          </div>
        )}

        {/* The mode at inline-start, the next view in the middle: above the
            bar, which keeps its four buttons. */}
        <div style={cs.viewRow}>
          <div style={cs.viewRowStart}>
            <FieldModePill mode={fieldMode} onOpen={() => setModeOpen(true)} />
          </div>
          <button type="button" style={cs.viewChip} data-testid="field-view-chip" data-view={view}
            title={t('tip.camera.viewChip')} onClick={() => chooseView(cycleView(viewRef.current))}>
            {t('camera.nextView', { view: viewName })}
          </button>
          {/* What a metered account (an external researcher) has left to ask
              the AI: components/credits/AiCreditsBadge.js. */}
          <div style={cs.viewRowEnd}>
            <AiCreditsBadge user={user} tone="dark" testId="field-ai-credits" />
          </div>
        </div>

        {photos.length > 0 && (
          <ol className="fm-cam-tray" style={cs.tray} aria-label={t('camera.tray')} data-testid="field-tray">
            {photos.map((p, i) => (
              <li key={p.id} style={cs.thumb} data-testid={`field-tray-photo-${i}`} data-view={p.view}>
                {previews[p.id]
                  ? <img src={previews[p.id]} alt={t('camera.trayPhoto', { n: i + 1, view: t(`views.${p.view}`) })}
                      style={cs.thumbImg} />
                  : <span style={cs.thumbEmpty}>{i + 1}</span>}
                <span style={cs.thumbBadge} aria-hidden="true">{t(`viewsShort.${p.view}`)}</span>
                <button type="button" style={cs.thumbRemove} data-testid={`field-tray-remove-${i}`}
                  title={t('tip.camera.remove')}
                  aria-label={t('camera.removePhoto', { n: i + 1, view: t(`views.${p.view}`) })}
                  onClick={() => removePhoto(p.id)}>
                  <span style={cs.thumbRemoveDot} aria-hidden="true">×</span>
                </button>
              </li>
            ))}
          </ol>
        )}

        <div role="group" aria-label={t('camera.controls')} style={cs.bar} data-testid="field-camera-bar">
          <div style={cs.barSide}>
            <button type="button" style={cs.barBtn} data-testid="field-back" title={t('tip.camera.back')}
              aria-label={t('camera.back')} onClick={shown ? closeSheet : onLeave}>
              <span style={cs.barIcon} aria-hidden="true">{dir === 'rtl' ? '→' : '←'}</span>
              <span style={cs.barLabel}>{t('camera.backShort')}</span>
            </button>
            <button type="button" style={cs.barBtn} data-testid="field-gallery" title={t('tip.camera.gallery')}
              aria-label={t('camera.gallery')} aria-describedby="field-gallery-counts" aria-haspopup="dialog"
              onClick={() => { setNotesOpen(false); openSheet('gallery'); }}>
              <span style={cs.barIcon} aria-hidden="true">▦</span>
              <span style={cs.barLabel}>{t('camera.galleryShort')}</span>
              {counts.today > 0 && (
                <span style={cs.badge} aria-hidden="true" data-testid="field-gallery-today">{counts.today}</span>
              )}
              {counts.waiting > 0 && (
                <span style={cs.waitingBadge} aria-hidden="true" data-testid="field-gallery-waiting">
                  {counts.waiting}
                </span>
              )}
              <span id="field-gallery-counts" style={srOnly}>
                {t('camera.galleryCounts', { today: counts.today, waiting: counts.waiting })}
              </span>
            </button>
          </div>

          <button ref={snapRef} type="button" style={cs.snap} data-testid="field-snap" title={t('tip.camera.snap')}
            aria-label={shotLabel(tc, modes.mode, modes.frames) || t('camera.snap')} aria-busy={modes.busy || undefined}
            data-mode={modes.mode} onClick={snap}>
            <span style={cs.snapInner} aria-hidden="true" />
            {/* What this press takes, where it is not a single photo. */}
            <ShotBadge capture={modes} style={cs.snapBadge} testId="field-snap-shot" />
          </button>

          <div style={{ ...cs.barSide, justifyContent: 'flex-end' }}>
            {/* aria-disabled, not disabled: a disabled button takes the focus
                with it, and a tap on it would say nothing. */}
            <button type="button" style={{ ...cs.barBtn, opacity: photos.length ? 1 : 0.45 }}
              data-testid="field-new-batch" title={t('tip.camera.newBatch')} aria-label={t('camera.newBatch')}
              aria-disabled={!photos.length} onClick={finish}>
              <span style={cs.barIcon} aria-hidden="true">✓</span>
              <span style={cs.barLabel}>{t('camera.newBatchShort')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* The way to the target: a third layer, beside the top and the bottom,
          so it is never clipped by the top nor laid over the capture bar.
          Away while the notes, a sheet or the summary is open: it would lie
          over them. */}
      {target && !notesOpen && !shown && !summary && (
        <FieldTargetMap target={target} fix={fix} gpsState={gpsState} open={targetOpen}
          routeState={routeState} mode={travelMode} onMode={setTravelMode}
          onToggle={() => setTargetOpen(o => !o)} />
      )}
      {/* A live field event: the team's mini-map in a corner; one tap opens
          the event's own screen (Part III section 19). */}
      {eventLive?.live?.phase === 'live' && !notesOpen && !shown && !summary && (
        <FieldMiniMap fix={fix} positions={eventLive.live.positions || []}
          waypoint={eventLive.live.me?.next_waypoint || null}
          onExpand={() => navigate(`/field/event/${eventLive.missionId}`)} />
      )}

      {/* The specimen just saved: its summary and the AI's answer, before
          it is sent. */}
      {summary && (
        <FieldCaptureSummary key={summary.id} store={store} outbox={outbox} userId={userId} user={user}
          batch={summary.batch} mission={mission} target={target} auto={autoAi}
          onAuto={(on) => { setAutoAi(on); if (on) wakeRef.current({ force: true }); }}
          aiEnv={aiEnv} aiEnvPending={aiEnvPending} aiPaused={aiPaused}
          onAiError={(e) => { if (e?.status === 429) setAiPaused(true); }}
          onDone={closeSummary} onEdit={editSummary} />
      )}

      {encircled?.stillUrl && (
        <LiveIdentifyPanel crop={encircled.crop} user={user} side={encircled.side} offset={barHeight}
          aiEnv={aiEnv} waiting={aiEnvPending} requestConfig={KEEP}
          onError={(e) => { if (e?.status === 429) setAiPaused(true); }}
          onClose={() => closeEncircled()} onKeep={keepFrame} kept={encircled.kept}
          handoff={batch?.handoff || null} location={here ? { lat: here.lat, lng: here.lng } : null}
          mayCollect={canCollect && observing} onHandoff={keepLiveHandoff} onCollect={collectLive}
          onResult={heardLive} traitAnswers={traitAnswers} onTraitAnswer={answerTrait}
          traitFraming={encircled.traitFraming} mayRecordTraits={mayRecordAnswers} traitsHeld={!batch}
          testId="field-live-ai" />
      )}
      {traitLook && !encircled && !shown && !summary && !modeOpen && !notesOpen && !zstack.active && !annotating && (
        <FieldTraitLook asks={fieldTraitAsks(traitLook.result)} answers={traitAnswers} onAnswer={answerTrait}
          onDismiss={() => setTraitLook(null)}
          onPhotographBase={batch?.photos?.some(p => p.view === 'stipe_base') ? undefined : () => chooseView('stipe_base')}
          warned={acceptBlockedReason(traitLook.result) === 'dangerous'} mayRecord={mayRecordAnswers} held={!batch}
          offset={barHeight} testId="field-traits-look" />
      )}

      {/* The sheets: one at a time, from the address (useFieldSheet). */}
      <Sheet open={shown === 'gallery'} side="right" title={t('gallery.title')} onClose={closeSheet}
        testId="field-sheet-gallery" actions={mayTasks ? (
          <button type="button" style={cs.sheetAction} data-testid="field-gallery-tasks" title={t('tasks.open')}
            onClick={() => openSheet('tasks')}>
            {t('tasks.chip')}
          </button>
        ) : null}>
        <div style={cs.sheetList}>
          <FieldGalleryList store={store} outbox={outbox} userId={userId} user={user} headingAs="h3" />
        </div>
      </Sheet>
      <Sheet open={shown === 'hub'} side="left" title={t('sheet.messages')} onClose={closeSheet}
        testId="field-sheet-hub">
        <ChatHub user={user} selected={hubChannel} onSelect={setHubChannel} narrow testId="field-chat"
          style={{ height: '100%' }} />
      </Sheet>
      <FieldTasksSheet open={shown === 'tasks'} onClose={closeSheet} user={user} tasks={tasks}
        focusMission={sheetValues.mission} onStart={closeSheet} />
      {/* Beside the picture, not over it: a tap there still focuses, and
          the shutter below it still shoots. */}
      <Sheet open={shown === 'pro'} side="bottom" modal={false} title={tc('title')} onClose={closeSheet}
        offset={`${barHeight}px`} size="min(48dvh, 420px)" testId="field-adjust-sheet">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {/* Quick actions first (D17): Identify for whoever may ask the AI
              (the middle of the picture; a loop drawn round a mushroom does
              the same), Annotate for whoever may mark photos, once this
              specimen has one. The thermal file is the summary's and the
              editor's (D23), not the camera's. */}
          {(mayEncircle || (mayMark && photos.length > 0)) && (
            <div role="group" aria-label={t('quick.label')} style={cs.quickRow} data-testid="field-quick-actions">
              {mayEncircle && (
                <div style={cs.quickItem}>
                  <button type="button" style={cs.quick} onClick={identifyCentre} title={tai('live.centreTip')}
                    aria-describedby="field-quick-circle" data-testid="field-quick-identify">
                    <span aria-hidden="true">🔍 </span>{t('quick.identify')}
                  </button>
                  <span id="field-quick-circle" style={cs.quickHint} data-testid="field-quick-circle">
                    {t('quick.circleHint')}
                  </span>
                </div>
              )}
              {mayMark && photos.length > 0 && (
                <div style={cs.quickItem}>
                  <button ref={annotateRef} type="button" style={cs.quick} onClick={() => setAnnotating(true)}
                    title={t('quick.annotateTip')}
                    aria-haspopup="dialog" aria-expanded={annotating} data-testid="field-quick-annotate"
                    data-marked={marked}>
                    <span aria-hidden="true">✏️ </span>
                    {marked ? t('quick.annotateMarked', { count: marked }) : t('quick.annotate')}
                  </button>
                </div>
              )}
            </div>
          )}
          <CameraControls controls={controls} meter={meter} onResetMeter={resetMeter} testIdPrefix="field-camera"
            onReset={() => modes.setMode('single')} />
          <CaptureModes capture={modes} zstack={zstack} testIdPrefix="field-camera" />
          <DetectorTrial detector={detector} on={detectorOn} onToggle={setDetectorOn} manifest={detectorManifest} />
        </div>
      </Sheet>

      {/* Annotate (the drawer's quick action): this specimen's photos, marked
          on the workbench's canvas and kept with the batch. */}
      {annotating && photos.length > 0 && (
        <FieldAnnotate photos={photos} urls={previews} initial={batch?.annotations || {}}
          initialPhotoId={photos[photos.length - 1]?.id} onSave={saveAnnotations}
          onClose={() => setAnnotating(false)} />
      )}

      {/* A Z-stack taken: its layers to keep or discard. */}
      {(zstack.run.phase === 'review' || zstack.run.phase === 'failed') && (
        <Dialog title={tc('zstack.title')} onClose={zstack.discard} width={520} testId="field-zstack-dialog">
          <ZStackReview zstack={zstack} testIdPrefix="field-camera" onKeep={stack => stack && addStack(stack)} />
        </Dialog>
      )}

      {/* Collection or Observation: at the root, above every camera layer. */}
      <FieldModeDialog open={modeOpen} mode={fieldMode} onChange={changeMode} onClose={() => setModeOpen(false)}
        canCollect={canCollect} batchOpen={photos.length > 0} onApplyToBatch={applyModeToBatch} />

      {/* The phone's own camera, for when this page cannot show one. No
          `hidden` attribute: some browsers will not open a picker for it. */}
      <input ref={fileRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
        tabIndex={-1} aria-hidden="true" data-testid="field-file-input" onChange={onFile} />
      <div aria-live="polite" style={srOnly} data-testid="field-live">{announcement}</div>
    </CameraViewport>
  );
}

// Always dark, whatever the theme: it sits over a live picture.
const BAR = 'rgba(0, 0, 0, 0.55)';
// The bottom bar's own background, darker than the chips': its 12px labels
// are white on a translucent white button, over whatever the camera sees.
// Over a white frame, 0.55 left them at about 3.6:1; at 0.72 the button
// composites to about #616161, about 6.2:1 for white text (WCAG 1.4.3).
const BAR_STRONG = 'rgba(0, 0, 0, 0.72)';

// A phone on its side, or any short screen: the photo strip goes, and the
// bar packs tighter, so the shutter row always fits. (iOS ignores the
// manifest's portrait lock, and a browser tab never had one.)
const LANDSCAPE_CSS = '@media (orientation: landscape) and (max-height: 500px){'
  + '.fm-cam-tray{display:none!important}'
  // The mini-map to a target goes too; its caption (the distance) stays.
  + '.fm-target-map-box{display:none!important}'
  + '.fm-cam-bottom{padding-top:6px!important;gap:6px!important}}';

// A chip that is a button: a 44px target, white on dark.
const CHIP_BUTTON = {
  minHeight: 44, padding: '0 16px', borderRadius: 999, border: '1px solid rgba(255, 255, 255, 0.6)',
  background: 'rgba(0, 0, 0, 0.35)', color: 'var(--c-on-accent)', font: 'inherit', fontSize: 15,
  fontWeight: 600, cursor: 'pointer',
};

const cs = {
  // Fixed to the viewport rather than 100vh: on a phone 100vh runs under the
  // browser's own bars. 100dvh where it is understood.
  root: {
    // Below the View as banner while there is one (--view-as-offset).
    position: 'fixed', inset: 0, top: 'var(--view-as-offset, 0px)',
    height: 'calc(100dvh - var(--view-as-offset, 0px))', overflow: 'hidden', background: 'black',
    color: 'var(--c-on-accent)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
  },
  video: { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' },
  flash: { position: 'absolute', inset: 0, background: 'rgba(255, 255, 255, 0.85)', pointerEvents: 'none', zIndex: 3 },
  // The encircle's frozen frame, laid exactly over the video, and the loop over it.
  still: { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 1,
    pointerEvents: 'none' },
  loopLayer: { position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 1, pointerEvents: 'none' },
  // The Pro drawer's quick actions: a row at its top, each a 44px button.
  quickRow: { display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-start' },
  quickItem: { display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: '1 1 160px' },
  quick: {
    minHeight: 44, padding: '8px 14px', borderRadius: 8, cursor: 'pointer', font: 'inherit',
    fontSize: 15, fontWeight: 600, border: '1px solid var(--c-primary)', background: 'transparent',
    color: 'var(--c-primary-fg)', textAlign: 'start',
  },
  quickHint: { fontSize: 13, lineHeight: 1.35, color: 'var(--c-text-muted-fg)' },
  // May shrink and clip; the bottom bar never does.
  top: {
    position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6,
    minHeight: 0, flexShrink: 1, overflow: 'hidden',
    // A tap between the chips reaches the picture (tap to focus); each chip and sheet takes its own back.
    pointerEvents: 'none',
    paddingTop: 'calc(10px + env(safe-area-inset-top))', paddingBottom: 16,
    paddingLeft: 'max(12px, env(safe-area-inset-left))', paddingRight: 'max(12px, env(safe-area-inset-right))',
    background: 'linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0))',
  },
  // The GPS chip at inline-start, the Map chip at inline-end.
  topRow: {
    display: 'flex', alignSelf: 'stretch', alignItems: 'center', justifyContent: 'space-between', gap: 8,
  },
  chip: {
    display: 'inline-flex', alignItems: 'center', minHeight: 32, padding: '4px 12px', borderRadius: 999,
    background: BAR, fontSize: 14, fontWeight: 600, pointerEvents: 'none',
  },
  // On the bottom bar's darker background: it sits over a bright sky as often
  // as not (about 6:1 for its white text).
  mapChip: { ...CHIP_BUTTON, background: BAR_STRONG, flexShrink: 0, pointerEvents: 'auto' },
  // The target chip and its ×, one pill: the label may be long, the × never goes.
  targetGroup: { display: 'inline-flex', alignItems: 'center', minWidth: 0, maxWidth: '100%', borderRadius: 999,
    border: '1px solid #f48fb1', background: BAR_STRONG, pointerEvents: 'auto' },
  targetChip: { ...CHIP_BUTTON, border: 'none', background: 'none', display: 'inline-flex', alignItems: 'center',
    minWidth: 0, paddingInlineEnd: 4, whiteSpace: 'nowrap' },
  targetLabel: { overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '40vw' },
  targetClear: { minWidth: 44, minHeight: 44, padding: 0, border: 'none', background: 'none', color: 'var(--c-on-accent)',
    font: 'inherit', fontSize: 20, cursor: 'pointer', flexShrink: 0 },
  // The field-notes sheet: over the viewfinder under the chips, dark like the
  // bar, the one place on the camera with something to type.
  notesSheet: { margin: '6px 8px 0', padding: 10, borderRadius: 12, background: BAR_STRONG, color: '#fff',
    maxWidth: 520, boxSizing: 'border-box', pointerEvents: 'auto' },
  // A side sheet's handle: a 44px-wide target on the physical edge, below
  // the maps that float under the chips, around a slim visible tab.
  handle: {
    position: 'absolute', zIndex: 2, top: 'calc(50% + 40px)', transform: 'translateY(-50%)', width: 44,
    height: 72, padding: 0, border: 'none', background: 'none', cursor: 'pointer', display: 'flex',
    alignItems: 'center', justifyContent: 'center', color: 'var(--c-on-accent)', font: 'inherit',
  },
  handleTab: {
    position: 'relative', width: 30, height: 60, display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: BAR_STRONG, border: '1px solid rgba(255, 255, 255, 0.5)', fontSize: 18, lineHeight: 1,
  },
  handleDot: {
    position: 'absolute', top: 6, right: 4, width: 9, height: 9, borderRadius: '50%',
    background: 'var(--c-danger, #c0392b)', border: '1px solid #fff',
  },
  // The controls sheet's grip: over the picture just above the bar.
  handleBottom: {
    position: 'absolute', bottom: '100%', left: 0, right: 0, margin: '0 auto', width: 88, height: 44,
    padding: 0, border: 'none', background: 'none', cursor: 'pointer', display: 'flex', alignItems: 'flex-end',
    justifyContent: 'center', paddingBottom: 8,
  },
  handleGrip: { width: 48, height: 6, borderRadius: 999, background: 'rgba(255, 255, 255, 0.85)',
    boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.35)' },
  toastOverGrip: { marginBottom: 48 },
  // The gallery's list in its sheet, spaced as on its page.
  sheetList: { display: 'flex', flexDirection: 'column', gap: 14 },
  // A sheet's header button (the gallery's Tasks): on the sheet's light surface, not the camera's dark.
  sheetAction: { minHeight: 44, padding: '0 14px', borderRadius: 999, border: '1px solid var(--c-border)',
    background: 'var(--c-primary-soft-3)', color: 'var(--c-text-body-fg)', fontSize: 14, cursor: 'pointer' },
  notesText: { width: '100%', boxSizing: 'border-box', borderRadius: 8, border: '1px solid rgba(255,255,255,0.4)',
    padding: 8, font: 'inherit', fontSize: 15, background: 'rgba(255,255,255,0.95)', color: '#111', resize: 'vertical' },
  notice: {
    margin: 0, maxWidth: 520, padding: '8px 12px', borderRadius: 10, background: BAR, fontSize: 14, lineHeight: 1.4,
  },
  alert: { background: 'rgba(160, 20, 20, 0.88)', fontWeight: 600 },
  bottom: {
    position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', gap: 10, flexShrink: 0,
    paddingTop: 10, paddingBottom: 'calc(12px + env(safe-area-inset-bottom))',
    paddingLeft: 'max(12px, env(safe-area-inset-left))', paddingRight: 'max(12px, env(safe-area-inset-right))',
    background: BAR_STRONG,
  },
  // Where the toast is, but it takes taps: Stop and Cancel.
  zstackRun: {
    position: 'absolute', bottom: '100%', left: 12, right: 12, margin: '0 auto 8px', maxWidth: 520,
    padding: '8px 12px', borderRadius: 10, background: 'rgba(0, 0, 0, 0.75)',
  },
  toast: {
    position: 'absolute', bottom: '100%', left: 0, right: 0, width: 'fit-content', margin: '0 auto 8px',
    maxWidth: 'min(520px, calc(100% - 24px))', padding: '8px 14px', borderRadius: 10, textAlign: 'center',
    background: 'rgba(0, 0, 0, 0.75)', fontSize: 15, lineHeight: 1.4, pointerEvents: 'none',
  },
  viewRow: { display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 8 },
  viewRowStart: { display: 'flex', justifyContent: 'flex-start', minWidth: 0 },
  viewRowEnd: { display: 'flex', justifyContent: 'flex-end', minWidth: 0 },
  viewChip: CHIP_BUTTON,
  tray: {
    display: 'flex', gap: 8, margin: 0, padding: 0, listStyle: 'none', overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
  },
  thumb: {
    position: 'relative', flex: '0 0 auto', width: 64, height: 64, borderRadius: 8, overflow: 'hidden',
    border: '2px solid rgba(255, 255, 255, 0.7)', background: 'rgba(255, 255, 255, 0.1)',
  },
  thumbImg: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  thumbEmpty: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' },
  thumbBadge: {
    position: 'absolute', bottom: 0, insetInlineStart: 0, insetInlineEnd: 0, padding: '1px 3px',
    background: 'rgba(0, 0, 0, 0.65)', fontSize: 11, fontWeight: 600, textAlign: 'center',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },
  // A 44px target around a small visible dot, at the thumbnail's top corner.
  thumbRemove: {
    position: 'absolute', top: -10, insetInlineEnd: -10, width: 44, height: 44, padding: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    border: 'none', background: 'none', cursor: 'pointer', color: 'var(--c-on-accent)', font: 'inherit',
  },
  thumbRemoveDot: {
    width: 24, height: 24, lineHeight: '22px', borderRadius: '50%', fontSize: 18, textAlign: 'center',
    background: 'rgba(0, 0, 0, 0.75)', border: '1px solid rgba(255, 255, 255, 0.8)',
  },
  bar: { display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 8 },
  barSide: { display: 'flex', gap: 8, alignItems: 'center', minWidth: 0 },
  barBtn: {
    position: 'relative', minWidth: 56, minHeight: 56, padding: '4px 6px', borderRadius: 14, border: 'none',
    background: 'rgba(255, 255, 255, 0.14)', color: 'var(--c-on-accent)', font: 'inherit', cursor: 'pointer',
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2,
  },
  barIcon: { fontSize: 22, lineHeight: 1 },
  barLabel: { fontSize: 12, fontWeight: 600, lineHeight: 1.2, maxWidth: 84, textAlign: 'center' },
  badge: {
    position: 'absolute', top: -4, insetInlineEnd: -4, minWidth: 20, height: 20, padding: '0 5px',
    borderRadius: 999, background: 'var(--c-primary-2)', color: 'var(--c-on-accent)', fontSize: 12,
    fontWeight: 700, lineHeight: '20px', textAlign: 'center',
  },
  waitingBadge: {
    position: 'absolute', bottom: -4, insetInlineEnd: -4, minWidth: 20, height: 20, padding: '0 5px',
    borderRadius: 999, background: 'var(--c-warning)', color: 'black', fontSize: 12,
    fontWeight: 700, lineHeight: '20px', textAlign: 'center',
  },
  snap: {
    position: 'relative', width: 80, height: 80, borderRadius: '50%', padding: 0, cursor: 'pointer',
    border: '4px solid var(--c-on-accent)', background: 'rgba(255, 255, 255, 0.15)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  snapInner: { width: 60, height: 60, borderRadius: '50%', background: 'rgba(255, 255, 255, 0.92)' },
  snapBadge: { position: 'absolute', top: -6, insetInlineEnd: -6 },
};
